// This file defines the configuration for application fields.
// Any types that are used should continue to live in constants.ts
/* eslint-disable max-lines */
import {
  AddressAutocomplete,
  Application,
  ButtonListField,
  ButtonListFieldDirectionOption,
  CO_APPLICANT_ADDRESS_OPTIONS,
  CO_APPLICANT_TOGGLE_LIST,
  CoApplicantAddressMatch,
  CoApplicantFormState,
  CREDIT_CARD_CREDIT_SCORE_LIST,
  CREDIT_CARD_PURPOSE_GROUPS,
  CREDIT_CARD_PURPOSES_LIST,
  CREDIT_SCORE_LIST,
  DEFAULT_REDEMPTION_TYPES,
  DIRECT_DEPOSIT_LIST,
  DynamicSelectField,
  EDUCATION_LEVEL_LIST,
  EMPLOYMENT_STATUS_LIST,
  FieldKey,
  FieldType,
  GENDER_OPTION_LIST,
  GRADUATE_DEGREE_TYPE_BUTTON_LIST,
  GRADUATE_DEGREE_TYPE_LIST,
  HEIGHT_OPTION_LIST,
  HiddenField,
  isUuid,
  LOAN_PURPOSE_LIST,
  Mask,
  MORTGAGE_TYPE_LIST,
  NUMBER_OF_UNITS_LIST,
  NumberField,
  OCCUPANCY_TYPE_LIST,
  PAY_FREQUENCY_LIST,
  PROPERTY_STATUS_LIST,
  PROPERTY_TYPE_LIST,
  PURCHASE_STATUS_LIST,
  RadioGroupField,
  SelectField,
  SliderInput,
  SLR_EDUCATION_LEVEL_BUTTON_LIST,
  SLR_EDUCATION_LEVEL_LIST,
  SLR_US_CITIZEN_OR_GREENCARD_OPTION_LIST,
  TextField,
  UniversityAutocomplete,
  UNSECURED_DEBT_RESPONSES_LIST,
  USES_TOBACCO_OPTION_LIST,
  VEHICLE_ID_TYPE_LIST,
  ZipcodeField,
} from 'embed/constants'
import { LifeInsuranceApplication } from 'embed/reducers/application'
import {
  BankRoutingNumberValidator,
  CurrencyValidator,
  DateMonthAndYearOnlyValidator,
  DateValidator,
  DOBErrorMessage,
  DOBValidatorAgeCap,
  DOBValidatorMinAge,
  EmailValidator,
  LoanAmountValidator,
  PasswordValidator,
  PhoneValidator,
  SocialSecurityValidator,
  TrueValidator,
  UsStateAbbreviationValidator,
  ValidationData,
  VehicleIdNumberValidator,
  WeightValidator,
  ZipcodeValidator,
} from 'embed/services/Validation'
import { getResponsePathWithSteps } from 'embed/utils/life-insurance'

import {
  BooleanOptions,
  ConfigServiceDataKeys,
  Filter,
  GoogleAnalyticsActions,
  LoanPurposeOptions,
  Product,
} from '@evenfinancial/even-ts-static'

export interface FieldsType {
  AddressOne: AddressAutocomplete
  AddressTwo: TextField
  AnnualIncomeCredit: TextField
  AnnualIncomeLoan: TextField
  ApplyWithCoApplicant: RadioGroupField
  ApplyWithCoApplicantButtonList: ButtonListField
  Assets: TextField
  AutomobileLoanBalance: TextField
  AutoRefinanceLoanAmount: TextField
  AvailableAssets: TextField
  BankRoutingNumber: TextField
  CreditCardDebt: TextField
  CreditCardPurposesButtonList: ButtonListField
  CreditCardPurposes: SelectField
  CreditCardRewardsGroupsButtonList: ButtonListField
  CitizenshipStatus: SelectField
  City: TextField
  ClosingDate: TextField
  CoApplicantAddressMatch: RadioGroupField
  CoApplicantAddressMatchButtonList: ButtonListField
  CoApplicantAddressOne: AddressAutocomplete
  CoApplicantAddressTwo: TextField
  CoApplicantAnnualIncome: TextField
  CoApplicantAnnualIncomeLoan: TextField
  CoApplicantCity: TextField
  CoApplicantDateOfBirth: TextField
  CoApplicantFirstName: TextField
  CoApplicantLastName: TextField
  CoApplicantState: TextField
  CoApplicantZipcode: ZipcodeField
  ConsentsToFCRA: HiddenField
  ConsentsToTCPA: HiddenField
  CreditRating: SelectField
  CreditRatingButtonList: ButtonListField
  DateOfBirth: TextField
  DepositAmount: SelectField
  DirectDeposit: ButtonListField
  DownPaymentAmount: TextField
  Education: SelectField
  EducationButtonList: ButtonListField
  EmailAddress: TextField
  EmploymentStatus: SelectField
  EmploymentStatusButtonList: ButtonListField
  EstimatedMileage: TextField
  FirstName: TextField
  Gender: ButtonListField
  GraduateDegree: SelectField
  GraduateDegreeButtonList: ButtonListField
  GraduationDate: TextField
  HeightInInches: SelectField
  LastName: TextField
  LenderId: TextField
  LoanAmount: TextField
  LoanAmountSlider: SliderInput
  AnnualIncomeLoanSlider: SliderInput
  LoanPurpose: DynamicSelectField
  MonthlyDebt: TextField
  MonthlyHousingPayment: TextField
  MonthlyIncome: TextField
  MonthlyPayment: TextField
  NumberOfUnits: SelectField
  Password: TextField
  PayFrequency: SelectField
  PayFrequencyButtonList: ButtonListField
  PhoneNumber: TextField
  PropertyAddressOne: TextField
  PropertyAddressTwo: TextField
  PropertyCity: TextField
  PropertyCounty: SelectField
  PropertyState: TextField
  PropertyStatus: SelectField
  PropertyStatusButtonList: ButtonListField
  PropertyType: SelectField
  PropertyUse: SelectField
  PropertyZipcode: NumberField
  PurchasePrice: TextField
  PurchaseStatus: SelectField
  RedemptionTypesButtonList: ButtonListField
  SLREducation: SelectField
  SLREducationButtonList: ButtonListField
  University: UniversityAutocomplete
  SocialSecurityNumber: TextField
  State: TextField
  StudentLoanAmount: TextField
  TobaccoSmoker: ButtonListField
  TotalUnsecuredDebt: ButtonListField
  TransactionType: SelectField
  VehicleIdentificationNumber: TextField
  VehicleIdType: SelectField
  VehicleYear: SelectField
  VehicleMake: SelectField
  VehicleModel: SelectField
  VehicleTrim: SelectField
  Zipcode: ZipcodeField
  WeightInPounds: NumberField
}
export const Fields: FieldsType = {
  AddressOne: new AddressAutocomplete(Filter.AddressOne, {
    label: 'Street Address 1',
    required: true,
    defaultValidationMessage: 'Please enter your street address',
    placeholder: 'Enter your street address',
  }),
  AddressTwo: new TextField(Filter.AddressTwo, {
    placeholder: 'apt, unit, etc',
    label: 'Street Address 2',
    required: false,
  }),
  AnnualIncomeCredit: new TextField(Filter.AnnualIncome, {
    inputMode: 'numeric',
    label: 'Annual income',
    mask: Mask.AnnualIncome,
    placeholder: 'Income per year in $USD',
    required: true,
    tooltip:
      'List your yearly income before taxes. Alimony, child support, or separate maintenance is optional and does not need to be included if you do not wish it to be considered as a basis for payment towards the outstanding balance. Increase any non-taxable income by 25%.',
    validators: [{ validator: new CurrencyValidator() }],
    defaultValidationMessage: 'Please enter your annual income',
  }),
  // For loan applications, we need to add the bolded text in the tooltip for compliance reasons
  AnnualIncomeLoan: new TextField(Filter.AnnualIncome, {
    inputMode: 'numeric',
    label: 'Annual income',
    mask: Mask.AnnualIncome,
    placeholder: 'Income per year in $USD',
    required: true,
    tooltip:
      'List your total available income including wages, retirement, investments, and rental properties. Alimony, child support or separate maintenance is optional and does not need to be included if you do not wish it to be considered as a basis for repaying the loan. Increase any non-taxable income or benefits by 25%.',
    validators: [{ validator: new CurrencyValidator() }],
    defaultValidationMessage: 'Please enter your annual income',
  }),
  AnnualIncomeLoanSlider: new SliderInput(Filter.AnnualIncome, {
    canOverflowMaxAmount: true,
    initialValue: 5000,
    inputMode: 'numeric',
    label: 'Income per year in $USD',
    mask: Mask.AnnualIncome,
    maxAmount: 200000,
    minAmount: 0,
    required: true,
    tooltip:
      'List your total available income including wages, retirement, investments, and rental properties. Alimony, child support or separate maintenance is optional and does not need to be included if you do not wish it to be considered as a basis for repaying the loan. Increase any non-taxable income or benefits by 25%.',
    validators: [{ validator: new CurrencyValidator() }],
    defaultValidationMessage: 'Please enter your annual income',
  }),
  ApplyWithCoApplicantButtonList: new ButtonListField(Filter.HasCoApplicant, {
    hideValidationStatus: true,
    label: 'Applying with a Co-Applicant?',
    required: true,
    selectOptions: CO_APPLICANT_TOGGLE_LIST,
    tooltip:
      'A co-applicant is an additional person considered in the underwriting and approval of a loan who shares legal responsibility for the loan. Applying for a loan with a co-applicant may help to improve the chances of a loan approval and may provide for more favorable loan terms.',
  }),
  ApplyWithCoApplicant: new RadioGroupField(Filter.ApplyWithCoApplicant, {
    defaultValidationMessage: 'Please select an option',
    initialValue: BooleanOptions.False,
    label: 'Applying with a Co-Applicant?',
    required: true,
    tooltip:
      'A co-applicant is an additional person considered in the underwriting and approval of a loan who shares legal responsibility for the loan. Applying for a loan with a co-applicant may help to improve the chances of a loan approval and may provide for more favorable loan terms.',
    children: [
      {
        checked: false,
        label: 'Yes',
        value: BooleanOptions.True,
        name: Filter.ApplyWithCoApplicant,
        pathToStore: Filter.HasCoApplicant,
        trackingEvent: {
          action: GoogleAnalyticsActions.ToggledCoapplicant,
          data: CoApplicantFormState.SHOWN,
        },
      },
      {
        checked: true,
        label: 'No',
        value: BooleanOptions.False,
        name: Filter.ApplyWithCoApplicant,
        pathToStore: Filter.HasCoApplicant,
        trackingEvent: {
          action: GoogleAnalyticsActions.ToggledCoapplicant,
          data: CoApplicantFormState.HIDDEN,
        },
      },
    ],
  }),
  Assets: new TextField(Filter.Assets, {
    inputMode: 'numeric',
    label: 'Total Assets',
    mask: Mask.Currency,
    required: true,
    validators: [{ validator: new CurrencyValidator() }],
  }),
  AutomobileLoanBalance: new TextField(Filter.AutomobileLoanBalance, {
    inputMode: 'numeric',
    label: 'Auto Loan Balance',
    mask: Mask.AutomobileLoanBalance,
    placeholder: 'Auto loan balance in $USD',
    required: true,
    validators: [{ validator: new CurrencyValidator() }],
    tooltip: 'The amount of your auto loan that you are looking to refinance.',
  }),
  AvailableAssets: new TextField(Filter.AvailableAssets, {
    inputMode: 'numeric',
    label: 'Assets in Savings, Investments, Retirement, Checking',
    mask: Mask.Currency,
    placeholder: 'Enter Estimated Total',
    required: true,
    tooltip: 'The total of your savings, investments, retirement accounts, and average checking account balances.',
    validators: [{ validator: new CurrencyValidator() }],
  }),
  BankRoutingNumber: new TextField(Filter.BankRoutingNumber, {
    defaultValidationMessage: 'Please enter a valid bank routing number',
    inputMode: 'text',
    label: 'Routing Number',
    mask: Mask.BankRoutingNumber,
    placeholder: 'Enter Routing Number',
    required: true,
    tooltip: 'Your routing number can be found at the bottom of your checks, to the left of your account number.',
    helperText:
      'This information is required to find the best personalized offers. We employ industry-leading security practices to keep your data secure.',
    validators: [{ validator: new BankRoutingNumberValidator() }],
  }),
  CitizenshipStatus: new SelectField(Filter.CitizenshipStatus, {
    label: 'Citizenship Status',
    required: true,
    searchable: false,
    selectOptions: SLR_US_CITIZEN_OR_GREENCARD_OPTION_LIST,
  }),
  CreditCardDebt: new TextField(Filter.CreditCardDebt, {
    inputMode: 'numeric',
    label: 'Estimated Credit Card Debt',
    mask: Mask.Currency,
    placeholder: 'Enter Estimated Total',
    required: true,
    tooltip:
      'Providing your estimated total credit card debt better enables some of our partners to provide personalized loan offers that work best for your needs.',
    validators: [{ validator: new CurrencyValidator() }],
  }),
  CreditCardPurposes: new SelectField(Filter.CardPurposes, {
    label: 'Card Category',
    required: true,
    searchable: false,
    selectOptions: CREDIT_CARD_PURPOSES_LIST,
    defaultValidationMessage: 'Please select your card category',
  }),
  CreditCardPurposesButtonList: new ButtonListField(Filter.CardPurposes, {
    hideValidationStatus: true,
    required: true,
    selectOptions: CREDIT_CARD_PURPOSES_LIST,
  }),
  CreditCardRewardsGroupsButtonList: new ButtonListField(Filter.CardPurposes, {
    hideValidationStatus: true,
    required: true,
    selectOptions: CREDIT_CARD_PURPOSE_GROUPS,
  }),
  City: new TextField(Filter.City, {
    autocomplete: 'address-level2',
    inputMode: 'text',
    label: 'City',
    placeholder: 'City',
    defaultValidationMessage: 'Please enter a valid city',
    required: true,
  }),
  ClosingDate: new TextField(Filter.ClosingDate, {
    inputMode: 'numeric',
    label: 'Closing Date',
    mask: Mask.Date.Full,
    pipe: Mask.Pipe.Date.Full,
    placeholder: 'MM/DD/YYYY',
    required: true,
    validators: [{ validator: new DateValidator() }],
  }),
  CoApplicantAddressMatchButtonList: new ButtonListField(Filter.CoApplicantAddressMatch, {
    hideValidationStatus: true,
    label: 'Co-Applicant Address',
    required: true,
    selectOptions: CO_APPLICANT_ADDRESS_OPTIONS,
  }),
  CoApplicantAddressMatch: new RadioGroupField(Filter.CoApplicantAddressMatch, {
    initialValue: CoApplicantAddressMatch.SameAsApplicant,
    label: 'Co-Applicant Address',
    required: true,
    children: [
      {
        label: 'Same as Applicant',
        value: CoApplicantAddressMatch.SameAsApplicant,
        name: Filter.CoApplicantAddressMatch,
        pathToStore: Filter.CoApplicantAddressMatch,
        trackingEvent: {
          action: GoogleAnalyticsActions.ToggledCoapplicantAddress,
          data: CoApplicantFormState.HIDDEN,
        },
      },
      {
        label: 'Different from Applicant',
        pathToStore: Filter.CoApplicantAddressMatch,
        value: CoApplicantAddressMatch.DifferentFromApplicant,
        name: Filter.CoApplicantAddressMatch,
        trackingEvent: {
          action: GoogleAnalyticsActions.ToggledCoapplicantAddress,
          data: CoApplicantFormState.SHOWN,
        },
      },
    ],
  }),
  CoApplicantAddressOne: new AddressAutocomplete(Filter.CoApplicantAddressOne, {
    label: 'Co-Applicant Street Address 1',
    required: true,
    defaultValidationMessage: "Please enter the co-applicant's street address",
    placeholder: "Enter the co-applicant's street address",
  }),
  CoApplicantAddressTwo: new TextField(Filter.CoApplicantAddressTwo, {
    inputMode: 'text',
    label: 'Co-Applicant Street Address 2',
    required: false,
    placeholder: 'apt, unit, etc',
  }),
  CoApplicantAnnualIncome: new TextField(Filter.CoApplicantAnnualIncome, {
    inputMode: 'numeric',
    label: 'Co-Applicant Annual Income',
    mask: Mask.AnnualIncome,
    placeholder: 'Income per year in $USD',
    required: true,
    tooltip:
      "List the co-applicant's total available income including wages, retirement, " +
      'investments, and rental properties. Alimony, child support or separate ' +
      'maintenance is optional. Increase any non-taxable income or benefits by 25%.',
    validators: [{ validator: new CurrencyValidator() }],
    defaultValidationMessage: "Please enter the co-applicant's annual income",
  }),
  // For loan applications, we need to add the bolded text in the tooltip for compliance reasons
  CoApplicantAnnualIncomeLoan: new TextField(Filter.CoApplicantAnnualIncome, {
    inputMode: 'numeric',
    label: 'Co-Applicant Annual Income',
    mask: Mask.AnnualIncome,
    placeholder: 'Income per year in $USD',
    required: true,
    tooltip:
      "List the co-applicant's total available income including wages, retirement, investments, and rental properties. Alimony, child support or separate maintenance is optional and does not need to be included if you do not wish it to be considered as a basis for repaying the loan. Increase any non-taxable income or benefits by 25%.",
    validators: [{ validator: new CurrencyValidator() }],
    defaultValidationMessage: "Please enter the co-applicant's annual income",
  }),
  CoApplicantCity: new TextField(Filter.CoApplicantCity, {
    inputMode: 'text',
    label: 'Co-Applicant City',
    placeholder: "Co-applicant's City",
    required: true,
    defaultValidationMessage: "Please enter the co-applicant's city",
  }),
  CoApplicantDateOfBirth: new TextField(Filter.CoApplicantDateOfBirth, {
    inputMode: 'numeric',
    label: "Co-Applicant's Date of Birth",
    mask: Mask.Date.Full,
    pipe: Mask.Pipe.Date.Full,
    placeholder: 'MM/DD/YYYY',
    required: true,
    validators: [
      { validator: new DateValidator() },
      { validator: new DOBValidatorMinAge(), message: 'Co-applicant must be 18 or older to apply' },
    ],
    defaultValidationMessage: 'Please enter a valid date of birth',
  }),
  CoApplicantFirstName: new TextField(Filter.CoApplicantFirstName, {
    inputMode: 'text',
    label: 'Co-Applicant First Name',
    placeholder: "Co-applicant's first name",
    required: true,
    defaultValidationMessage: "Please enter the co-applicant's first name",
  }),
  CoApplicantLastName: new TextField(Filter.CoApplicantLastName, {
    inputMode: 'text',
    label: 'Co-Applicant Last Name',
    placeholder: "Co-applicant's last name",
    required: true,
    defaultValidationMessage: "Please enter the co-applicant's last name",
  }),
  CoApplicantState: new TextField(Filter.CoApplicantState, {
    inputMode: 'text',
    label: 'Co-Applicant State',
    placeholder: 'e.g. NY',
    mask: Mask.State,
    pipe: Mask.Pipe.State,
    required: true,
    validators: [{ validator: new UsStateAbbreviationValidator() }],
    defaultValidationMessage: 'Please enter a valid state abbreviation',
  }),
  CoApplicantZipcode: new ZipcodeField(Filter.CoApplicantZipcode, {
    inputMode: 'numeric',
    label: 'Co-Applicant Zip Code',
    placeholder: "Co-applicant's Zip Code",
    required: true,
    validators: [{ validator: new ZipcodeValidator() }],
    defaultValidationMessage: "Please enter the co-applicant's zip code",
  }),
  ConsentsToFCRA: new HiddenField(Filter.ConsentsToFCRA, {
    required: true,
    validators: [{ validator: new TrueValidator() }],
  }),
  ConsentsToTCPA: new HiddenField(Filter.ConsentsToTCPA, {
    required: true,
    validators: [{ validator: new TrueValidator() }],
  }),
  CreditRating: new SelectField(Filter.CreditScore, {
    initialValue: undefined,
    label: 'Credit Rating',
    searchable: false,
    required: true,
    selectOptions: CREDIT_SCORE_LIST,
    tooltip: 'Select what you think your credit score is.',
    defaultValidationMessage: 'Please select a credit score',
  }),
  CreditRatingButtonList: new ButtonListField(Filter.CreditScore, {
    hideValidationStatus: true,
    label: 'Credit Rating',
    required: true,
    selectOptions: CREDIT_CARD_CREDIT_SCORE_LIST,
    tooltip: 'Select what you think your credit score is.',
  }),
  DateOfBirth: new TextField(Filter.DateOfBirth, {
    autocomplete: 'birthday',
    inputMode: 'numeric',
    label: 'Date of Birth',
    mask: Mask.Date.Full,
    pipe: Mask.Pipe.Date.Full,
    placeholder: 'MM/DD/YYYY',
    required: true,
    validators: [
      { validator: new DateValidator() },
      { validator: new DOBValidatorAgeCap(), message: DOBErrorMessage() },
      { validator: new DOBValidatorMinAge(), message: 'You must be 18 or older to apply' },
    ],
    defaultValidationMessage: 'Please enter a valid date of birth',
  }),
  DepositAmount: new SelectField(Filter.DepositAmountIntentRange, {
    label: 'Deposit Amount',
  }),
  DirectDeposit: new ButtonListField(Filter.DirectDeposit, {
    label: 'Direct Deposit',
    required: true,
    selectOptions: DIRECT_DEPOSIT_LIST,
  }),
  DownPaymentAmount: new TextField(Filter.DownPaymentAmount, {
    inputMode: 'numeric',
    label: 'Down Payment Amount',
    mask: Mask.Currency,
    required: true,
    validators: [{ validator: new CurrencyValidator() }],
  }),
  Education: new SelectField(Filter.EducationLevel, {
    label: 'Highest Level of Education',
    searchable: false,
    required: true,
    selectOptions: EDUCATION_LEVEL_LIST,
  }),
  EducationButtonList: new ButtonListField(Filter.EducationLevel, {
    hideValidationStatus: true,
    label: 'Highest Level of Education',
    required: true,
    selectOptions: EDUCATION_LEVEL_LIST,
  }),
  EmailAddress: new TextField(Filter.Email, {
    autocomplete: 'email',
    inputMode: 'text',
    label: 'Email Address',
    placeholder: 'Please enter your email address',
    required: true,
    validators: [{ validator: new EmailValidator() }],
    defaultValidationMessage: 'Please enter a valid e-mail address',
  }),
  EmploymentStatus: new SelectField(Filter.EmploymentStatus, {
    label: 'Employment Status',
    required: true,
    searchable: false,
    selectOptions: EMPLOYMENT_STATUS_LIST,
    defaultValidationMessage: 'Please select your employment status',
  }),
  EmploymentStatusButtonList: new ButtonListField(Filter.EmploymentStatus, {
    hideValidationStatus: true,
    label: 'Employment Status',
    required: true,
    selectOptions: EMPLOYMENT_STATUS_LIST,
    defaultValidationMessage: 'Please select your employment status',
  }),
  EstimatedMileage: new TextField(Filter.EstimatedMileage, {
    inputMode: 'numeric',
    label: 'Estimated Mileage',
    required: true,
    mask: Mask.EstimatedMileage,
    placeholder: 'Estimated number of miles',
    tooltip: 'Enter the number of miles your vehicle has driven at this time.',
  }),
  FirstName: new TextField(Filter.FirstName, {
    inputMode: 'text',
    label: 'First Name',
    placeholder: 'Your first name',
    autocomplete: 'given-name',
    required: true,
    defaultValidationMessage: 'Please enter your first name',
  }),
  Gender: new ButtonListField(Filter.Gender, {
    hideValidationStatus: true,
    required: true,
    selectOptions: GENDER_OPTION_LIST,
    direction: ButtonListFieldDirectionOption.Row,
  }),
  GraduateDegree: new SelectField(Filter.GraduateDegreeType, {
    label: 'Graduate Degree Type',
    required: true,
    selectOptions: GRADUATE_DEGREE_TYPE_LIST,
  }),
  GraduateDegreeButtonList: new ButtonListField(Filter.GraduateDegreeType, {
    hideValidationStatus: true,
    label: 'Graduate Degree Type',
    required: true,
    selectOptions: GRADUATE_DEGREE_TYPE_BUTTON_LIST,
  }),
  GraduationDate: new TextField(Filter.GraduationDate, {
    inputMode: 'numeric',
    label: 'Graduation Date or Last Attended',
    mask: Mask.Date.MonthAndYearOnly,
    pipe: Mask.Pipe.Date.MonthAndYearOnly,
    placeholder: 'MM/YYYY',
    required: true,
    tooltip: 'Graduation Date or last month when you attended school',
    validators: [{ validator: new DateMonthAndYearOnlyValidator() }],
  }),
  HeightInInches: new SelectField(Filter.HeightInInches, {
    label: 'Height',
    required: true,
    selectOptions: HEIGHT_OPTION_LIST,
  }),
  LastName: new TextField(Filter.LastName, {
    autocomplete: 'family-name',
    inputMode: 'text',
    label: 'Last Name',
    placeholder: 'Your last name',
    required: true,
    defaultValidationMessage: 'Please enter your last name',
  }),
  LenderId: new TextField(Filter.LenderId, {
    inputMode: 'text',
  }),
  LoanAmount: new TextField(Filter.LoanAmount, {
    initialValue: '',
    inputMode: 'numeric',
    label: 'Loan Amount',
    placeholder: 'Up to $250,000',
    mask: Mask.Currency,
    required: true,
    tooltip: 'Enter the loan amount you are seeking.',
    validators: [{ validator: new LoanAmountValidator() }],
    defaultValidationMessage: (data: ValidationData) => 'Please enter a number up to $250,000',
  }),
  StudentLoanAmount: new TextField(Filter.LoanAmount, {
    initialValue: '',
    inputMode: 'numeric',
    label: 'Refinance Amount',
    placeholder: 'Up to $500,000',
    mask: Mask.Currency,
    required: true,
    tooltip: 'Enter the total amount of existing student loans you’d like to refinance.',
    validators: [{ validator: new LoanAmountValidator() }],
    // @TODO: update params to satisfy linter requirement or comment to why this needs to stay as is
    defaultValidationMessage: (data: ValidationData) => 'Please enter a number up to $500,000',
  }),
  AutoRefinanceLoanAmount: new TextField(Filter.LoanAmount, {
    initialValue: '',
    inputMode: 'numeric',
    label: 'Loan Balance',
    placeholder: 'Up to $250,000',
    mask: Mask.Currency,
    required: true,
    tooltip:
      'Tell us the remaining balance on your current auto loan, so we can match you with personalized refinancing offers.',
    validators: [{ validator: new LoanAmountValidator() }],
    // @TODO: update params to satisfy linter requirement or comment to why this needs to stay as is
    defaultValidationMessage: (data: ValidationData) => 'Please enter a number up to $250,000',
  }),
  LoanAmountSlider: new SliderInput(Filter.LoanAmount, {
    initialValue: 5000,
    inputMode: 'numeric',
    label: 'Loan Amount',
    mask: Mask.Currency,
    maxAmount: (data: ValidationData) => (data.LoanPurpose === LoanPurposeOptions.STUDENT_REFINANCE ? 500000 : 250000),
    minAmount: 0,
    required: true,
    tooltip: 'Enter the loan amount you are seeking.',
    validators: [{ validator: new LoanAmountValidator() }],
    defaultValidationMessage: (data: ValidationData) =>
      data.LoanPurpose === LoanPurposeOptions.STUDENT_REFINANCE
        ? 'Please enter a number up to $500,000'
        : 'Please enter a number up to $250,000',
  }),
  LoanPurpose: new DynamicSelectField(Filter.LoanPurpose, {
    label: 'Loan Purpose',
    required: true,
    optionValuesConfigKey: ConfigServiceDataKeys.loanPurposeOptions,
    optionBoundingSet: LOAN_PURPOSE_LIST,
    defaultValidationMessage: 'Please select a loan purpose',
  }),
  MonthlyDebt: new TextField(Filter.MonthlyDebt, {
    inputMode: 'numeric',
    label: 'Monthly Debt',
    mask: Mask.Currency,
    required: true,
    validators: [{ validator: new CurrencyValidator() }],
  }),
  MonthlyHousingPayment: new TextField(Filter.MonthlyHousingPayment, {
    inputMode: 'numeric',
    label: 'Monthly Rent or Mortgage Payment',
    required: true,
    placeholder: 'Enter your monthly payment',
    mask: Mask.Currency,
    tooltip: 'Your personal total paid for your monthly rent or mortgage',
    validators: [{ validator: new CurrencyValidator() }],
  }),
  MonthlyIncome: new TextField(Filter.MonthlyIncome, {
    inputMode: 'numeric',
    label: 'Monthly Income',
    mask: Mask.Currency,
    required: true,
    validators: [{ validator: new CurrencyValidator() }],
  }),
  MonthlyPayment: new TextField(Filter.MonthlyPayment, {
    inputMode: 'numeric',
    label: 'Monthly Loan Payment',
    placeholder: 'Monthly loan payment in $USD',
    required: true,
    mask: Mask.MonthlyPayment,
    validators: [{ validator: new CurrencyValidator() }],
    tooltip: 'The current total amount you pay each month for your vehicle.',
  }),
  NumberOfUnits: new SelectField(Filter.NumberOfUnits, {
    label: 'Number of Units',
    required: true,
    selectOptions: NUMBER_OF_UNITS_LIST,
  }),
  Password: new TextField(
    Filter.Password,
    {
      hideValidationStatus: true,
      label: 'Password',
      tooltip:
        'Your password must be between 8 and 64 characters and include at least one capital letter and one number',
      validators: [{ validator: new PasswordValidator() }],
      defaultValidationMessage:
        'Your password must: <ul><li>Be between 8 and 64 characters</li><li>Include at least one capital letter</li><li>Include at least one number</li></ul>',
    },
    FieldType.Password,
  ),
  PayFrequency: new SelectField(Filter.PayFrequency, {
    label: 'Pay Frequency',
    required: true,
    searchable: false,
    selectOptions: PAY_FREQUENCY_LIST,
    tooltip: 'How often are you paid?',
    defaultValidationMessage: 'Please select a pay schedule',
  }),
  PayFrequencyButtonList: new ButtonListField(Filter.PayFrequency, {
    hideValidationStatus: true,
    label: 'Pay Frequency',
    required: true,
    selectOptions: PAY_FREQUENCY_LIST,
    defaultValidationMessage: 'Please select a pay schedule',
  }),
  PhoneNumber: new TextField(Filter.PrimaryPhone, {
    autocomplete: 'tel',
    inputMode: 'numeric',
    label: 'Phone Number',
    mask: Mask.PhoneNumber,
    placeholder: '(###) ###-####',
    required: true,
    tooltip:
      'We ask this so that we, our partners and their affiliated companies can ' +
      'contact you about the products and services you inquired about, even if your ' +
      'telephone number is listed on any Do-Not-Call list.  Contact may be made ' +
      'through automatic dialing systems, artificial or prerecorded voice messaging, ' +
      'or text message.',
    validators: [{ validator: new PhoneValidator() }],
    defaultValidationMessage: 'Please enter a valid telephone number',
  }),
  PropertyAddressOne: new TextField(Filter.PropertyAddressOne, {
    autocomplete: 'address-line1',
    inputMode: 'text',
    placeholder: 'Enter the street address',
    label: 'Property Street Address 1',
    required: true,
  }),
  PropertyAddressTwo: new TextField(Filter.PropertyAddressTwo, {
    autocomplete: 'address-line2',
    inputMode: 'text',
    placeholder: 'apt, unit, etc',
    label: 'Property Street Address 2',
    required: false,
  }),
  PropertyCity: new TextField(Filter.PropertyCity, {
    autocomplete: 'address-level2',
    inputMode: 'text',
    label: 'Property City',
    required: true,
  }),
  PropertyCounty: new SelectField(Filter.PropertyCounty, {
    label: 'Property County',
    required: true,
    selectOptions: [],
  }),
  PropertyState: new TextField(Filter.PropertyState, {
    autocomplete: 'address-level1',
    inputMode: 'text',
    label: 'Property State',
    required: true,
    placeholder: 'e.g. NY',
    mask: Mask.State,
    pipe: Mask.Pipe.State,
    validators: [{ validator: new UsStateAbbreviationValidator() }],
    defaultValidationMessage: 'Please enter a valid state abbreviation',
  }),
  PropertyStatus: new SelectField(Filter.PropertyStatus, {
    label: 'Do You Rent or Own Your Home?',
    searchable: false,
    required: true,
    selectOptions: PROPERTY_STATUS_LIST,
  }),
  PropertyStatusButtonList: new ButtonListField(Filter.PropertyStatus, {
    hideValidationStatus: true,
    label: 'Do You Rent or Own Your Home?',
    required: true,
    selectOptions: PROPERTY_STATUS_LIST,
  }),
  PropertyType: new SelectField(Filter.PropertyType, {
    label: 'Property Type',
    required: true,
    selectOptions: OCCUPANCY_TYPE_LIST,
  }),
  PropertyUse: new SelectField(Filter.PropertyUse, {
    label: 'Property Use',
    required: true,
    selectOptions: PROPERTY_TYPE_LIST,
  }),
  PropertyZipcode: new NumberField(Filter.PropertyZipcode, {
    inputMode: 'numeric',
    label: 'Property Zip Code',
    required: true,
    validators: [{ validator: new ZipcodeValidator() }],
  }),
  PurchasePrice: new TextField(Filter.PurchasePrice, {
    inputMode: 'numeric',
    label: 'Purchase Price',
    mask: Mask.Currency,
    required: true,
    validators: [{ validator: new CurrencyValidator() }],
  }),
  PurchaseStatus: new SelectField(Filter.PurchaseStatus, {
    label: 'Purchase Status',
    required: true,
    selectOptions: PURCHASE_STATUS_LIST,
  }),
  RedemptionTypesButtonList: new ButtonListField(Filter.RewardsPreference, {
    required: true,
    selectOptions: DEFAULT_REDEMPTION_TYPES,
  }),
  SLREducation: new SelectField(Filter.EducationLevel, {
    label: 'Highest Level of Education',
    searchable: false,
    required: true,
    selectOptions: SLR_EDUCATION_LEVEL_LIST,
  }),
  SLREducationButtonList: new ButtonListField(Filter.EducationLevel, {
    label: 'Highest Level of Education',
    required: true,
    selectOptions: SLR_EDUCATION_LEVEL_BUTTON_LIST,
    hideValidationStatus: true,
  }),
  University: new UniversityAutocomplete(Filter.University, {
    label: 'University Attended',
    placeholder: 'Enter university name',
    defaultValidationMessage: 'Please enter the name of the university you attended',
    required: true,
  }),
  SocialSecurityNumber: new TextField(Filter.SocialSecurityNumber, {
    inputMode: 'numeric',
    label: 'Social Security',
    mask: Mask.SocialSecurityNumber,
    placeholder: '###-##-####',
    required: true,
    tooltip:
      'We use your social security number to validate your information and check your credit. ' +
      'This will NOT affect your credit score. Your information is transmitted securely.',
    validators: [{ validator: new SocialSecurityValidator() }],
    defaultValidationMessage: 'Please enter a valid social security number to receive personalized loan offers',
  }),
  State: new TextField(Filter.State, {
    autocomplete: 'address-level1',
    inputMode: 'text',
    label: 'State',
    mask: Mask.State,
    pipe: Mask.Pipe.State,
    placeholder: 'e.g. NY',
    required: true,
    validators: [{ validator: new UsStateAbbreviationValidator() }],
    defaultValidationMessage: 'Please enter a valid state abbreviation',
  }),
  TobaccoSmoker: new ButtonListField(Filter.TobaccoSmoker, {
    hideValidationStatus: true,
    required: true,
    selectOptions: USES_TOBACCO_OPTION_LIST,
    direction: ButtonListFieldDirectionOption.Row,
  }),
  TotalUnsecuredDebt: new ButtonListField(Filter.TotalUnsecuredDebt, {
    hideValidationStatus: true,
    required: true,
    helperText: 'Do not include debt from mortgages, auto loans, or other types of secured loans.',
    selectOptions: UNSECURED_DEBT_RESPONSES_LIST,
  }),
  TransactionType: new SelectField(Filter.TransactionType, {
    label: 'Transaction Type',
    required: true,
    selectOptions: MORTGAGE_TYPE_LIST,
  }),
  VehicleMake: new SelectField(Filter.VehicleMake, {
    label: 'Make',
    required: true,
  }),
  VehicleModel: new SelectField(Filter.VehicleModel, {
    label: 'Model',
    required: true,
  }),
  VehicleTrim: new SelectField(Filter.VehicleTrim, {
    label: 'Trim',
    required: true,
  }),
  VehicleYear: new SelectField(Filter.VehicleYear, {
    label: 'Year',
    required: true,
  }),
  VehicleIdentificationNumber: new TextField(Filter.VehicleIdentificationNumber, {
    inputMode: 'text',
    label: 'Vehicle Identification Number.',
    required: true,
    placeholder: 'e.g. 4Y1SL65848Z411439',
    validators: [{ validator: new VehicleIdNumberValidator() }],
    defaultValidationMessage: 'Please enter a 17 digit VIN',
    tooltip:
      "You may find your VIN at the base of the windshield on the driver's side, the driver's side door jam and/or your vehicle’s insurance or registration card.",
  }),
  VehicleIdType: new SelectField(Filter.VehicleIdType, {
    label: 'Vehicle ID Type',
    required: true,
    searchable: false,
    selectOptions: VEHICLE_ID_TYPE_LIST,
    tooltip: 'Lenders need this information in order to understand the current value of your vehicle.',
  }),
  WeightInPounds: new TextField(Filter.WeightInPounds, {
    inputMode: 'numeric',
    label: 'Weight',
    mask: Mask.Weight,
    pipe: Mask.Pipe.Weight,
    required: true,
    placeholder: 'Enter your weight in lbs',
    validators: [{ validator: new WeightValidator() }],
  }),
  Zipcode: new ZipcodeField(Filter.Zipcode, {
    autocomplete: 'postal-code',
    inputMode: 'numeric',
    label: 'Zip Code',
    placeholder: 'Zip Code',
    required: true,
    validators: [{ validator: new ZipcodeValidator() }],
    defaultValidationMessage: 'Please enter a valid zip code',
  }),
}

export enum SummaryFieldType {
  SummaryField,
  SummaryFieldGroup,
}

// Fields that are displayed by the Plaid Summary card.
// Field Groups represent sets of fields that are only displayed if
// they are all present.
export const SUMMARY_FIELDS = [
  {
    field: Fields.EmailAddress,
    type: SummaryFieldType.SummaryField,
  },
  {
    fields: [Fields.FirstName, Fields.LastName],
    type: SummaryFieldType.SummaryFieldGroup,
  },
  {
    fields: [Fields.AddressOne, Fields.AddressTwo, Fields.Zipcode, Fields.City, Fields.State],
    type: SummaryFieldType.SummaryFieldGroup,
  },
  {
    field: Fields.PhoneNumber,
    type: SummaryFieldType.SummaryField,
  },
  {
    field: Fields.AnnualIncomeLoan,
    type: SummaryFieldType.SummaryField,
  },
  {
    field: Fields.PayFrequency,
    type: SummaryFieldType.SummaryField,
  },
]

export const getInvalidFields = (application: Application | LifeInsuranceApplication, product: Product): FieldKey[] => {
  const invalidFieldKeys: string[] = []
  if (
    product === Product.LifeInsurance &&
    'routerQuestions' in application &&
    application.routerQuestions !== undefined &&
    'machineConfig' in application.routerQuestions &&
    application.routerQuestions.machineConfig !== undefined &&
    application.fields !== undefined
  ) {
    const { fields } = application
    const routerQuestionsInResponsePath = new Set(
      getResponsePathWithSteps(application.routerQuestions.machineConfig, fields).responsePath.map(
        (item) => item.currentState,
      ),
    )
    for (const fieldKey in fields) {
      if (fields.hasOwnProperty(fieldKey)) {
        const fieldData = fields[fieldKey]
        if (
          (isUuid(fieldKey) && routerQuestionsInResponsePath.has(fieldKey) && fieldData?.isValid === false) ||
          (!isUuid(fieldKey) && fieldData?.isValid === false)
        ) {
          invalidFieldKeys.push(fieldKey)
        }
      }
    }
  } else {
    Object.entries(application.fields ?? {}).forEach(([fieldKey, fieldData]) => {
      if (fieldData.isValid === false) {
        invalidFieldKeys.push(fieldKey)
      }
    })
  }

  return invalidFieldKeys
}
